import React, { createContext, useContext, useRef, useState, useCallback, useMemo, ReactNode } from 'react';
import { AlertColor } from '@mui/material';

export interface NotificationItem {
  id: string;
  message: string;
  severity: AlertColor;
  autoHideDuration?: number;
  timestamp: string;
  read: boolean;
}

interface NotificationContextType {
  showNotification: (message: string, severity: AlertColor, autoHideDuration?: number) => void;
  clearNotification: (id: string) => void;
  dismissNotification: (id: string) => void;
  clearAllNotifications: () => void;
  /** Register a callback that runs when "Clear All" is used (e.g. to close error dialogs). Returns unregister. */
  registerClearAllListener: (callback: () => void) => () => void;
  markAllRead: () => void;
  notifications: NotificationItem[];
  unreadCount: number;
  latestNotification: NotificationItem | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const clearAllListenersRef = useRef<Set<() => void>>(new Set());

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearNotification = dismissNotification;

  const registerClearAllListener = useCallback((callback: () => void) => {
    clearAllListenersRef.current.add(callback);
    return () => {
      clearAllListenersRef.current.delete(callback);
    };
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications(() => []);
    clearAllListenersRef.current.forEach((cb) => cb());
  }, []);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map((notification) => ({ ...notification, read: true })));
  }, []);

  const showNotification = useCallback((message: string, severity: AlertColor, autoHideDuration: number = 6000) => {
    const id = Date.now().toString();
    const notification: NotificationItem = {
      id,
      message,
      severity,
      autoHideDuration,
      timestamp: new Date().toISOString(),
      read: false,
    };

    setNotifications(prev => [...prev, notification]);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const latestNotification = useMemo(
    () => notifications[notifications.length - 1] ?? null,
    [notifications]
  );

  const contextValue = useMemo(
    () => ({
      showNotification,
      clearNotification,
      dismissNotification,
      clearAllNotifications,
      registerClearAllListener,
      markAllRead,
      notifications,
      unreadCount,
      latestNotification,
    }),
    [
      showNotification,
      clearNotification,
      dismissNotification,
      clearAllNotifications,
      registerClearAllListener,
      markAllRead,
      notifications,
      unreadCount,
      latestNotification,
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
}; 